import { useEffect, useMemo, useState } from 'react';
import { Library, Boxes, FolderTree } from 'lucide-react';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Spinner,
} from '../../ui';
import { useKnowledge } from '../../features/knowledge/useKnowledge';
import { SearchPanel } from './SearchPanel';
import { PackageDetail } from './PackageDetail';

/**
 * Knowledge explorer — search Spec Studio packages/specs, browse a package's
 * revisions + documents, and read its Effort Insight (which can seed the
 * Estimation wizard).
 *
 * Strict layering: this page reaches data ONLY through the {@link useKnowledge}
 * feature hook; it never imports a gateway class or the api facade directly.
 * The mock-vs-real choice is entirely encapsulated behind the hook.
 */
export function KnowledgePage() {
  const {
    gateway,
    packages,
    collections,
    loading,
    error,
    refresh,
  } = useKnowledge();

  const [filter, setFilter] = useState('');
  const [selectedName, setSelectedName] = useState<string | null>(null);

  // Select the first package once loaded (if none selected yet).
  useEffect(() => {
    if (!selectedName && packages.length > 0) {
      setSelectedName(packages[0].name);
    }
  }, [packages, selectedName]);

  const filteredPackages = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return packages;
    return packages.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.description ?? '').toLowerCase().includes(term) ||
        (p.language ?? '').toLowerCase().includes(term) ||
        (p.team ?? '').toLowerCase().includes(term),
    );
  }, [packages, filter]);

  const selected = useMemo(
    () => packages.find((p) => p.name === selectedName) ?? null,
    [packages, selectedName],
  );

  const selectPackage = (name: string) => setSelectedName(name);

  return (
    <div>
      <PageHeader
        icon={<Library className="h-5 w-5" />}
        title="Knowledge"
        description="Explore Spec Studio packages and specs to inform estimates and architecture decisions."
        actions={
          <Badge tone="success" dot>
            Knowledge: Live
          </Badge>
        }
      />

      <SearchPanel
        gateway={gateway}
        filter={filter}
        onFilterChange={setFilter}
        onSelectPackage={selectPackage}
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading knowledge" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<Library className="h-6 w-6" />}
          title="Couldn't load knowledge"
          description={error}
          action={
            <button
              type="button"
              onClick={() => void refresh()}
              className="text-sm font-medium text-accent hover:underline"
            >
              Try again
            </button>
          }
        />
      ) : packages.length === 0 ? (
        <EmptyState
          icon={<Library className="h-6 w-6" />}
          title="No packages found"
          description="No Spec Studio packages are available to your account yet."
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
          {/* Left: package list + collections */}
          <div className="flex flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <Boxes className="h-4 w-4 text-zinc-500" />
                    Packages ({filteredPackages.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardBody className="flex flex-col gap-1.5">
                {filteredPackages.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-zinc-500">
                    No packages match “{filter}”.
                  </p>
                ) : (
                  filteredPackages.map((p) => {
                    const active = p.name === selectedName;
                    return (
                      <button
                        key={p.id || p.name}
                        type="button"
                        onClick={() => selectPackage(p.name)}
                        className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                          active
                            ? 'border-accent/50 bg-surface-3'
                            : 'border-transparent hover:bg-surface-3'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-zinc-100">
                            {p.name}
                          </span>
                          {p.language && (
                            <Badge tone="neutral">{p.language}</Badge>
                          )}
                        </div>
                        {p.description && (
                          <p className="mt-0.5 line-clamp-2 text-xs text-zinc-500">
                            {p.description}
                          </p>
                        )}
                      </button>
                    );
                  })
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <FolderTree className="h-4 w-4 text-zinc-500" />
                    Collections ({collections.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardBody className="flex flex-col gap-2">
                {collections.length === 0 ? (
                  <p className="text-sm text-zinc-500">No collections.</p>
                ) : (
                  collections.map((c) => (
                    <div
                      key={c.id}
                      className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5"
                    >
                      <div className="text-sm font-medium text-zinc-100">
                        {c.name}
                      </div>
                      {c.description && (
                        <p className="mt-0.5 text-xs text-zinc-500">
                          {c.description}
                        </p>
                      )}
                      {c.packageNames && c.packageNames.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {c.packageNames.map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => selectPackage(n)}
                              className="rounded-full border border-surface-border bg-surface-2 px-2 py-0.5 text-xs text-zinc-300 transition-colors hover:border-accent/40 hover:text-zinc-100"
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </div>

          {/* Right: package detail */}
          <div>
            {selected ? (
              <PackageDetail gateway={gateway} pkg={selected} />
            ) : (
              <EmptyState
                icon={<Boxes className="h-6 w-6" />}
                title="Select a package"
                description="Choose a package on the left to see its specs and effort insight."
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgePage;
